/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { QUERY_PARAMS, ROUTES } from '@futrli/shared-services';

import * as styles from '../onboarding-nav.scss';
import { ForecastType } from '../shared/onboarding-type-select';
import { SCENARIO_WITH_INVOICES_ONBOARDING_STEP } from '../const';
import { PeriodsMenu } from '../../detail/predictions/predictions-nav-data-controls/periods-menu';
import { NewAssumptionButton } from '../../detail/predictions/components/new-assumption-button';
import { CashFlowSwitch } from '../../detail/predictions/predictions-nav-data-controls/cash-flow-switch';
import { ForecastingMenu } from '../../detail/predictions/predictions-nav-data-controls/forecasts-menu';

export const ScenarioWithInvoicesOnboardingNav = () => {
  const { onboardingStep } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const forecastType = searchParams.get(QUERY_PARAMS.FORECAST_TYPE);
  const editInvoicesBills = searchParams.get(QUERY_PARAMS.EDIT_INVOICES_BILLS);

  let scenarioSteps;
  switch (forecastType) {
    case ForecastType.FromLYA:
      scenarioSteps = 12;
      break;
    case ForecastType.WithAutoPredictions:
      scenarioSteps = 13;
      break;
    case ForecastType.FromScratch:
      scenarioSteps = 11;
      break;
    default:
      scenarioSteps = 11;
      break;
  }

  const initialWidth = 100 / scenarioSteps;
  let onboardingStepNum = onboardingStep ? Number(onboardingStep) : 0;

  // If we are on step 2 and we have edit_invoices_bills query param set to true, we should display
  // progress bar width as it is step 3.
  if (
    onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED &&
    editInvoicesBills === 'true'
  ) {
    onboardingStepNum += 1;
  }
  const currentWidth = (onboardingStepNum + 1) * initialWidth;

  const showPeriods =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CONTROL_WHAT_YOU_SEE)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_WHAT_YOU_SEE)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CONTROL_WHAT_YOU_SEE));

  const showNewAssumption =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CUSTOMISE)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CUSTOMISE));

  const showCashFlowSwitchAccordingToSteps =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW));

  const hideCashFlowSwitchAccordingToPath =
    location.pathname.indexOf(ROUTES.CASH_FLOW_STATEMENT) !== -1 ||
    location.pathname.indexOf(ROUTES.BALANCE_SHEET) !== -1;

  const showCashFlowSwitch = showCashFlowSwitchAccordingToSteps && !hideCashFlowSwitchAccordingToPath;

  const showForecastControlsMenu =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_ADDITIONAL_CONTROLS)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_ADDITIONAL_CONTROLS)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum >= Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_ADDITIONAL_CONTROLS));

  const newAssumptionButtonIsOpen =
    (forecastType === ForecastType.FromScratch &&
      onboardingStepNum === Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CUSTOMISE)) ||
    (forecastType === ForecastType.WithAutoPredictions &&
      onboardingStepNum === Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE)) ||
    (forecastType === ForecastType.FromLYA &&
      onboardingStepNum === Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CUSTOMISE));

  const hideCashflowPlaceholder = hideCashFlowSwitchAccordingToPath;
  return (
    <>
      <nav className={styles.navContainer}>
        {showForecastControlsMenu ? (
          <ForecastingMenu />
        ) : (
          <div className={styles.forecastControlsPlaceholder} style={{ marginRight: 30 }} />
        )}
        <div className={styles.divider} />
        {showCashFlowSwitch ? (
          <>
            <div style={{ height: 34, marginRight: 18 }} />
            <CashFlowSwitch />
          </>
        ) : hideCashflowPlaceholder ? null : (
          <div className={styles.cashFlowPlaceholder} style={{ marginLeft: 30 }} />
        )}
        {showPeriods ? (
          <div style={{ marginLeft: 'auto' }}>
            <PeriodsMenu />
          </div>
        ) : (
          <>
            <div className={styles.periodsPlaceholder} style={{ marginLeft: 'auto' }} />
          </>
        )}

        {showNewAssumption ? (
          <NewAssumptionButton isOpenInitially={newAssumptionButtonIsOpen} />
        ) : (
          <div className={styles.newPredictionPlaceholder} style={{ marginLeft: 13 }} />
        )}
        <div style={{ width: 20 }} />
        <div className={styles.providerPlaceholder} />
      </nav>
      <div className={styles.progressiveBarContainer}>
        <div className={classNames(styles.progressBar)} style={{ width: `${currentWidth}%` }} />
      </div>
    </>
  );
};
